import type { InputAdapter, InputFrame } from './types'
import { EMPTY_INPUT } from './types'

export class DesktopAdapter implements InputAdapter {
  private enabled = false
  private keys = new Set<string>()
  private lookX = 0
  private lookY = 0
  private defuseHold = false
  private pauseEdge = false
  private cameraToggleEdge = false
  private checkEdge = false

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.enabled) return
    if (e.repeat) return
    this.keys.add(e.code)
    if (e.code === 'Escape') {
      this.pauseEdge = true
    }
    // C — переключение FPS/TPP
    if (e.code === 'KeyC') {
      this.cameraToggleEdge = true
    }
    // F — Check (aim-reveal), не hold
    if (e.code === 'KeyF') {
      this.checkEdge = true
    }
  }

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code)
  }

  private onMouseMove = (e: MouseEvent) => {
    if (!this.enabled) return
    if (document.pointerLockElement !== this.target) return
    this.lookX += e.movementX
    this.lookY += e.movementY
  }

  private onMouseDown = (e: MouseEvent) => {
    if (!this.enabled) return
    // ЛКМ или ПКМ — hold defuse
    if (e.button === 0 || e.button === 2) this.defuseHold = true
  }

  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0 || e.button === 2) this.defuseHold = false
  }

  private onContextMenu = (e: Event) => {
    if (this.enabled) e.preventDefault()
  }

  constructor(private target: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('mouseup', this.onMouseUp)
    target.addEventListener('mousedown', this.onMouseDown)
    target.addEventListener('contextmenu', this.onContextMenu)
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    if (!enabled) {
      this.keys.clear()
      this.lookX = 0
      this.lookY = 0
      this.defuseHold = false
    }
  }

  requestPointerLock(): void {
    if (document.pointerLockElement !== this.target) {
      void this.target.requestPointerLock()
    }
  }

  sample(_dt: number): InputFrame {
    const move = { x: 0, y: 0 }
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) move.x -= 1
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) move.x += 1
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) move.y += 1
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) move.y -= 1
    const len = Math.hypot(move.x, move.y)
    if (len > 1) {
      move.x /= len
      move.y /= len
    }

    const frame: InputFrame = {
      move,
      lookDelta: { x: this.lookX, y: this.lookY },
      defuseHold: this.defuseHold,
      check: this.checkEdge,
      pause: this.pauseEdge,
      cameraToggle: this.cameraToggleEdge,
    }

    this.lookX = 0
    this.lookY = 0
    this.pauseEdge = false
    this.cameraToggleEdge = false
    this.checkEdge = false

    if (!this.enabled) return { ...EMPTY_INPUT }
    return frame
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseup', this.onMouseUp)
    this.target.removeEventListener('mousedown', this.onMouseDown)
    this.target.removeEventListener('contextmenu', this.onContextMenu)
  }
}
